import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User } from '@/types'
import { resetEchoClient } from '@/realtime/echoClient'
import { useEnumsStore } from './enumsStore'
import { useOnboardingStore } from './onboardingStore'
import { AUTH_PERSIST_KEY, STORAGE_KEYS } from '@/utils/constants'
import { clearLastLogout } from '@/api/authDiagnostics'

export interface SetAuthOptions {
  expiresIn?: number
  rememberMe?: boolean
}

interface AuthState {
  user: User | null
  token: string | null
  expiresAt: number | null
  rememberMe: boolean
  isAuthenticated: boolean
  isImpersonating: boolean
  setAuth: (user: User, token: string, options?: SetAuthOptions) => void
  setAuthImpersonated: (user: User, token: string) => void
  updateToken: (token: string, expiresIn?: number) => void
  setImpersonating: (value: boolean) => void
  stopImpersonation: () => string | null
  logout: () => void
  setUser: (user: User) => void
}

function computeExpiresAt(expiresIn?: number): number | null {
  if (!expiresIn || expiresIn <= 0) {
    return null
  }

  return Date.now() + expiresIn * 1000
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      expiresAt: null,
      rememberMe: false,
      isAuthenticated: false,
      isImpersonating: false,
      setAuth: (user, token, options) => {
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token)
        resetEchoClient()
        // Сессия есть: причина прошлого выхода больше не актуальна
        // (вход по паролю уже передал её в prior_state)
        clearLastLogout()
        set((state) => ({
          user,
          token,
          expiresAt:
            options !== undefined && options.expiresIn !== undefined
              ? computeExpiresAt(options.expiresIn)
              : state.expiresAt,
          rememberMe: options?.rememberMe ?? state.rememberMe,
          isAuthenticated: true,
          isImpersonating: false,
        }))
      },
      setAuthImpersonated: (user, token) => {
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token)
        resetEchoClient()
        // Ответ гейта относится к прежнему пользователю: не сбросив его, админ
        // под чужим токеном получил бы редирект в мастер онбординга ученика.
        useOnboardingStore.getState().reset()
        set({ user, token, isAuthenticated: true, isImpersonating: true })
      },
      updateToken: (token, expiresIn) => {
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token)
        // Echo здесь намеренно НЕ пересоздаётся: пользователь тот же, меняется
        // только JWT, а его Echo читает из localStorage на каждый запрос
        // авторизации канала. Прежний resetEchoClient() рвал сокет на каждом
        // тихом обновлении токена (по таймеру и на любом 401) и убивал все
        // подписки — ученик, ждавший урок с открытой страницей, переставал
        // получать события, и никакой ошибки при этом не показывалось.
        set({
          token,
          expiresAt: computeExpiresAt(expiresIn),
        })
      },
      setImpersonating: (value) => set({ isImpersonating: value }),
      stopImpersonation: () => {
        const adminToken = localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN)
        if (!adminToken) return null
        localStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN)
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, adminToken)
        resetEchoClient()
        useOnboardingStore.getState().reset()
        set({
          token: adminToken,
          user: null,
          isAuthenticated: true,
          isImpersonating: false,
          expiresAt: null,
        })
        return adminToken
      },
      logout: () => {
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
        localStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN)
        resetEchoClient()
        useEnumsStore.getState().clearEnums()
        // Гейт онбординга привязан к пользователю: не сбросив его, следующий
        // вошедший на этой машине секунду видел бы чужой ответ.
        useOnboardingStore.getState().reset()
        set({
          user: null,
          token: null,
          expiresAt: null,
          rememberMe: false,
          isAuthenticated: false,
          isImpersonating: false,
        })
      },
      setUser: (user) => set({ user }),
    }),
    {
      // Другие вкладки подхватывают изменения по событию storage (authSessionSync)
      name: AUTH_PERSIST_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        expiresAt: state.expiresAt,
        rememberMe: state.rememberMe,
        isAuthenticated: state.isAuthenticated,
        isImpersonating: state.isImpersonating,
      }),
    }
  )
)
